import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { COMPETENCIES, APPROVAL_STATUSES, MILESTONE_LEVELS } from '../lib/constants';
import ComposeEmail from './ComposeEmail';

const ACTIVITY_LABELS = {
  teaching_lecture:'Teaching Lecture',case_report:'Case Report',
  mortality_morbidity:'Mortality & Morbidity',journal_club:'Journal Club',
  quality_improvement:'Quality Improvement Project',research_project:'Research Project',other:'Other',
};
const ACTIVITY_COLORS = {
  teaching_lecture:'#2D6A8A',case_report:'#3A7D6B',mortality_morbidity:'#8A4D5A',
  journal_club:'#6B4D8A',quality_improvement:'#8A6B3A',research_project:'#4D5A8A',other:'#6B6B6B',
};

export default function DirectorDashboard({ view, isMobile, isChairman, onEvaluate }) {
  const [fellows, setFellows]           = useState([]);
  const [faculty, setFaculty]           = useState([]);
  const [allEntries, setAllEntries]     = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedFellow, setSelectedFellow] = useState(null);
  const [reviewEntry, setReviewEntry]   = useState(null);
  const [reviewActivity, setReviewActivity] = useState(null);
  const [decision, setDecision]         = useState('');
  const [comment, setComment]           = useState('');
  const [saving, setSaving]             = useState(false);
  const [entriesTab, setEntriesTab]     = useState('cases');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderStatus, setReminderStatus]   = useState('');
  const [showCompose, setShowCompose]   = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: profiles }, { data: entries }, { data: activities }] = await Promise.all([
      supabase.from('profiles').select('*'),
      supabase.from('entries').select('*').order('created_at', { ascending: false }),
      supabase.from('activities').select('*').order('created_at', { ascending: false }),
    ]);
    setFellows((profiles||[]).filter(p => p.role === 'fellow'));
    setFaculty((profiles||[]).filter(p => p.role === 'faculty'));
    setAllEntries(entries || []);
    setAllActivities(activities || []);
    setLoading(false);
  };

  const sendReminder = async (fellow) => {
    setSendingReminder(fellow.id); setReminderStatus('');
    const fe = allEntries.filter(e => e.user_id === fellow.id);
    await supabase.rpc('send_fellow_reminder', {
      fellow_id: fellow.id, fellow_name_param: fellow.full_name, fellow_email_param: fellow.email,
      total_cases: fe.length,
      approved_cases: fe.filter(e=>['approved','approved_with_comments'].includes(e.approval_status)).length,
      pending_cases: fe.filter(e=>e.approval_status==='pending').length,
      rejected_cases: fe.filter(e=>e.approval_status==='rejected').length,
    });
    setSendingReminder(false); setReminderStatus('success');
    setTimeout(() => setReminderStatus(''), 3000);
  };

  const sendAllReminders = async () => {
    if (!window.confirm(`Send reminder emails to all ${fellows.length} fellows?`)) return;
    setSendingReminder('all'); setReminderStatus('');
    for (const fellow of fellows) {
      const fe = allEntries.filter(e => e.user_id === fellow.id);
      await supabase.rpc('send_fellow_reminder', {
        fellow_id: fellow.id, fellow_name_param: fellow.full_name, fellow_email_param: fellow.email,
        total_cases: fe.length,
        approved_cases: fe.filter(e=>['approved','approved_with_comments'].includes(e.approval_status)).length,
        pending_cases: fe.filter(e=>e.approval_status==='pending').length,
        rejected_cases: fe.filter(e=>e.approval_status==='rejected').length,
      });
    }
    setSendingReminder(false); setReminderStatus('success');
    setTimeout(() => setReminderStatus(''), 4000);
  };

  const submitCaseDecision = async () => {
    if (!decision) return;
    if ((decision==='approved_with_comments'||decision==='rejected') && !comment.trim()) { alert('Please add a comment.'); return; }
    setSaving(true);
    await supabase.from('entries').update({ approval_status:decision, faculty_comment:comment, reviewed_at:new Date().toISOString() }).eq('id', reviewEntry.id);
    setSaving(false); setReviewEntry(null); setDecision(''); setComment(''); fetchAll();
  };

  const submitActivityDecision = async () => {
    if (!decision) return;
    if (decision==='rejected' && !comment.trim()) { alert('Please add a comment.'); return; }
    setSaving(true);
    await supabase.from('activities').update({ approval_status:decision, director_comment:comment, reviewed_at:new Date().toISOString() }).eq('id', reviewActivity.id);
    setSaving(false); setReviewActivity(null); setDecision(''); setComment(''); fetchAll();
  };

  if (loading) return <div style={s.loading}>Loading program data…</div>;

  const fellowEntries = (id) => allEntries.filter(e => e.user_id === id);
  const compCount = (fid, cid) => fellowEntries(fid).filter(e => (e.competency_ids||[]).includes(cid)).length;
  const avgLevel = (id) => { const fe=fellowEntries(id); return fe.length?(fe.reduce((a,e)=>a+e.milestone_level,0)/fe.length).toFixed(1):'—'; };
  const comp = (id) => COMPETENCIES.find(c => c.id === id);

  // Case review panel
  if (reviewEntry) {
    const e=reviewEntry; const level=MILESTONE_LEVELS.find(l=>l.n===e.milestone_level); const status=APPROVAL_STATUSES[e.approval_status];
    return (
      <div style={{ background:'#F7F4F0', minHeight:'100vh', paddingBottom: isMobile?80:0 }}>
        <div style={{ background:'#fff', padding: isMobile?'14px 16px':'16px 40px', borderBottom:'1px solid #E8E4E0', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:10 }}>
          <button onClick={()=>{setReviewEntry(null);setDecision('');setComment('');}} style={s.backBtn}>← Back</button>
          <span style={{ fontFamily:"'DM Serif Display'", fontSize: isMobile?16:20, color:'#1A1A1F' }}>Review Case</span>
        </div>
        <div style={{ padding: isMobile?16:'24px 40px', maxWidth:1000, margin:'0 auto' }}>
          <div style={{ background:'#fff', borderRadius:12, padding: isMobile?14:'20px 24px', marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div><div style={{ fontFamily:"'DM Serif Display'",fontSize:18 }}>{e.fellow_name}</div><div style={{ fontSize:12,color:'#888' }}>{e.date} · {e.setting}</div></div>
              <div style={{ display:'flex',gap:6 }}>{e.is_difficult&&<span style={s.difficultBadge}>⚑</span>}<span style={{ fontSize:11,fontWeight:600,padding:'4px 10px',borderRadius:20,background:status?.bg,color:status?.color,border:`1px solid ${status?.border}` }}>{status?.label}</span></div>
            </div>
            {[{label:'MRN',val:e.mrn},{label:'Supervisors',val:(e.assigned_faculty_names||[]).join(', ')},{label:'Milestone',val:`Level ${e.milestone_level} — ${level?.label}`}].map((r,i)=>r.val?(
              <div key={i} style={{ display:'flex',gap:12,marginBottom:8 }}><span style={{ fontSize:11,fontWeight:700,color:'#999',textTransform:'uppercase',minWidth:90 }}>{r.label}</span><span style={{ fontSize:13,color:'#333' }}>{r.val}</span></div>
            ):null)}
            <div style={{ marginTop:12,paddingTop:12,borderTop:'1px solid #F0EDE8' }}><div style={{ fontSize:11,fontWeight:700,color:'#999',textTransform:'uppercase',marginBottom:6 }}>Case Summary</div><div style={{ fontSize:14,color:'#333',lineHeight:1.7 }}>{e.case_summary}</div></div>
            <div style={{ marginTop:12,paddingTop:12,borderTop:'1px solid #F0EDE8' }}><div style={{ fontSize:11,fontWeight:700,color:'#999',textTransform:'uppercase',marginBottom:8 }}>Competencies</div><div style={{ display:'flex',flexWrap:'wrap',gap:6 }}>{(e.competency_ids||[]).map(id=>{const c=comp(id);return c?<span key={id} style={{ padding:'4px 10px',borderRadius:20,fontSize:12,fontWeight:500,background:c.color,color:'#fff' }}>{c.label}</span>:null;})}</div></div>
          </div>
          {DecisionPanel({decision,setDecision,comment,setComment,saving,onSubmit:submitCaseDecision,includeApprovedWithComments:true})}
        </div>
      </div>
    );
  }

  // Activity review panel
  if (reviewActivity) {
    const a=reviewActivity; const actLabel=a.activity_type==='other'?`Other: ${a.activity_type_other}`:ACTIVITY_LABELS[a.activity_type]||a.activity_type; const color=ACTIVITY_COLORS[a.activity_type]||'#666';
    return (
      <div style={{ background:'#F7F4F0', minHeight:'100vh', paddingBottom: isMobile?80:0 }}>
        <div style={{ background:'#fff', padding: isMobile?'14px 16px':'16px 40px', borderBottom:'1px solid #E8E4E0', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:10 }}>
          <button onClick={()=>{setReviewActivity(null);setDecision('');setComment('');}} style={s.backBtn}>← Back</button>
          <span style={{ fontFamily:"'DM Serif Display'", fontSize: isMobile?16:20, color:'#1A1A1F' }}>Review Activity</span>
        </div>
        <div style={{ padding: isMobile?16:'24px 40px', maxWidth:1000, margin:'0 auto' }}>
          <div style={{ background:'#fff', borderRadius:12, padding: isMobile?14:'20px 24px', marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div><div style={{ fontFamily:"'DM Serif Display'",fontSize:18 }}>{a.fellow_name}</div><div style={{ fontSize:12,color:'#888' }}>{a.date}</div></div>
              <span style={{ fontSize:12,fontWeight:600,padding:'4px 12px',borderRadius:20,background:color+'18',color,border:`1px solid ${color}44` }}>{actLabel}</span>
            </div>
            <div style={{ fontSize:11,fontWeight:700,color:'#999',textTransform:'uppercase',marginBottom:6 }}>Title / Description</div>
            <div style={{ fontSize:14,color:'#333',lineHeight:1.7 }}>{a.title}</div>
          </div>
          {DecisionPanel({decision,setDecision,comment,setComment,saving,onSubmit:submitActivityDecision,includeApprovedWithComments:false})}
        </div>
      </div>
    );
  }

  // Analytics view
  if (view === 'analytics') {
    const total=allEntries.length; const overallAvg=total?(allEntries.reduce((a,e)=>a+e.milestone_level,0)/total).toFixed(1):'—';
    const byStatus=Object.keys(APPROVAL_STATUSES).map(k=>({...APPROVAL_STATUSES[k],key:k,count:allEntries.filter(e=>e.approval_status===k).length}));
    return (
      <div style={{ padding: isMobile?16:'36px 40px', maxWidth:1100, margin:'0 auto', paddingBottom: isMobile?80:36 }}>
        <h1 style={s.title}>Program Analytics</h1>
        <p style={s.sub}>{fellows.length} fellows · {faculty.length} faculty · {total} entries · {allActivities.length} activities</p>
        <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr 1fr':'repeat(4,1fr)', gap:12, marginBottom:20 }}>
          {[{label:'Cases',val:total},{label:'Activities',val:allActivities.length},{label:'Fellows',val:fellows.length},{label:'Avg Milestone',val:overallAvg}].map((m,i)=>(
            <div key={i} style={s.metricCard}><div style={s.metricVal}>{m.val}</div><div style={s.metricLabel}>{m.label}</div></div>
          ))}
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding: isMobile?14:'22px 24px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={s.cardTitle}>Case Approval Status</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:14 }}>
            {byStatus.map(st=>(
              <div key={st.key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderRadius:8, background:st.bg, border:`1px solid ${st.border}` }}>
                <span style={{ fontSize:13,fontWeight:600,color:st.color }}>{st.label}</span>
                <span style={{ fontSize:20,fontFamily:"'DM Serif Display'",color:st.color }}>{st.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Review entries view
  if (view === 'entries') {
    const pendingCases=allEntries.filter(e=>e.approval_status==='pending').length;
    const pendingActivities=allActivities.filter(a=>a.approval_status==='pending').length;
    return (
      <div style={{ padding: isMobile?16:'36px 40px', maxWidth:1100, margin:'0 auto', paddingBottom: isMobile?80:36 }}>
        <h1 style={s.title}>Review Entries</h1>
        <p style={s.sub}>{pendingCases} cases · {pendingActivities} activities pending review</p>
        <div style={{ display:'flex', gap:0, marginBottom:20, background:'#E8E4E0', borderRadius:10, padding:3, maxWidth:400 }}>
          {['cases','activities'].map(t=>(
            <button key={t} onClick={()=>setEntriesTab(t)}
              style={{ flex:1, padding:'9px', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'", background:entriesTab===t?'#fff':'transparent', color:entriesTab===t?'#1A3A5C':'#888', boxShadow:entriesTab===t?'0 1px 4px rgba(0,0,0,0.1)':'none' }}>
              {t==='cases'?'🏥 Cases':'📋 Activities'}
              {t==='cases'&&pendingCases>0&&<span style={{ background:'#C4622A',color:'#fff',borderRadius:20,fontSize:10,padding:'1px 6px',marginLeft:4 }}>{pendingCases}</span>}
              {t==='activities'&&pendingActivities>0&&<span style={{ background:'#C4622A',color:'#fff',borderRadius:20,fontSize:10,padding:'1px 6px',marginLeft:4 }}>{pendingActivities}</span>}
            </button>
          ))}
        </div>
        {entriesTab==='cases'&&(
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {allEntries.length===0&&<div style={s.empty}>No cases logged yet.</div>}
            {allEntries.map(e=>{const status=APPROVAL_STATUSES[e.approval_status];const isPending=e.approval_status==='pending';return(
              <div key={e.id} onClick={()=>isPending&&!isChairman&&setReviewEntry(e)} style={{ background:'#fff',borderRadius:10,padding: isMobile?'12px 14px':'14px 18px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',borderLeft:`3px solid ${status?.color||'#DDD'}`,cursor:(isPending&&!isChairman)?'pointer':'default' }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                  <div style={{ flex:1 }}><div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap' }}><span style={{ fontSize:13,fontWeight:700,color:'#2D6A8A' }}>{e.fellow_name}</span><span style={{ fontSize:11,color:'#999' }}>{e.date}</span><span style={{ fontSize:11,background:'#F0EDE8',color:'#666',padding:'2px 8px',borderRadius:20 }}>{e.setting}</span>{e.is_difficult&&<span style={s.difficultBadge}>⚑</span>}</div><div style={{ fontSize:13,color:'#333',lineHeight:1.5 }}>{(e.case_summary||'').slice(0,isMobile?80:120)}{(e.case_summary||'').length>(isMobile?80:120)?'…':''}</div></div>
                  <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,marginLeft:10 }}><span style={{ fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:20,background:status?.bg,color:status?.color,border:`1px solid ${status?.border}`,whiteSpace:'nowrap' }}>{status?.label}</span>{isPending&&!isChairman&&<span style={{ fontSize:11,color:'#2D6A8A',fontWeight:600 }}>Tap to review →</span>}</div>
                </div>
              </div>
            );})}
          </div>
        )}
        {entriesTab==='activities'&&(
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {allActivities.length===0&&<div style={s.empty}>No activities logged yet.</div>}
            {allActivities.map(a=>{const isPending=a.approval_status==='pending';const color=ACTIVITY_COLORS[a.activity_type]||'#666';const actLabel=a.activity_type==='other'?`Other: ${a.activity_type_other}`:ACTIVITY_LABELS[a.activity_type]||a.activity_type;const statusInfo={pending:{label:'⏳ Pending',color:'#C4622A',bg:'#FFF8EE',border:'#F4DFA8'},approved:{label:'✓ Approved',color:'#1E5C40',bg:'#F0FFF6',border:'#A8DCC0'},rejected:{label:'✗ Rejected',color:'#8A2D2D',bg:'#FFF0F0',border:'#F4A8A8'}}[a.approval_status];return(
              <div key={a.id} onClick={()=>isPending&&!isChairman&&setReviewActivity(a)} style={{ background:'#fff',borderRadius:10,padding: isMobile?'12px 14px':'14px 18px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',borderLeft:`3px solid ${color}`,cursor:(isPending&&!isChairman)?'pointer':'default' }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                  <div style={{ flex:1 }}><div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap' }}><span style={{ fontSize:13,fontWeight:700,color:'#2D6A8A' }}>{a.fellow_name}</span><span style={{ fontSize:11,color:'#999' }}>{a.date}</span><span style={{ fontSize:11,background:color+'18',color,padding:'2px 8px',borderRadius:20,fontWeight:500 }}>{actLabel}</span></div><div style={{ fontSize:13,color:'#333',lineHeight:1.5 }}>{a.title.slice(0,isMobile?80:120)}{a.title.length>(isMobile?80:120)?'…':''}</div></div>
                  <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,marginLeft:10 }}><span style={{ fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:20,background:statusInfo?.bg,color:statusInfo?.color,border:`1px solid ${statusInfo?.border}`,whiteSpace:'nowrap' }}>{statusInfo?.label}</span>{isPending&&!isChairman&&<span style={{ fontSize:11,color:'#2D6A8A',fontWeight:600 }}>Tap to review →</span>}</div>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    );
  }

  // All Fellows view (default)
  return (
    <div style={{ padding: isMobile?16:'36px 40px', maxWidth:1100, margin:'0 auto', paddingBottom: isMobile?80:36 }}>
      {showCompose && <ComposeEmail onClose={() => setShowCompose(false)} isMobile={isMobile} />}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
        <h1 style={{ ...s.title, marginBottom:0 }}>All Fellows</h1>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {!isChairman && (
            <>
              <button onClick={() => setShowCompose(true)}
                style={{ padding:'10px 16px', background:'#2D6A8A', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>
                ✉ Compose Message
              </button>
              <button onClick={sendAllReminders} disabled={!!sendingReminder || fellows.length===0}
                style={{ padding:'10px 16px', background: sendingReminder==='all'?'#888':'#1A3A5C', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'", opacity:fellows.length===0?0.5:1 }}>
                {sendingReminder==='all'?'⏳ Sending…':'📧 Remind All Fellows'}
              </button>
              {!isChairman && (
                <button onClick={onEvaluate}
                  style={{ padding:'10px 16px', background:'#4D5A8A', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>
                  ★ Evaluate Fellow
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <p style={s.sub}>{fellows.length} fellows · {faculty.length} faculty members</p>
      {reminderStatus==='success'&&<div style={{ background:'#F0FFF6',border:'1px solid #A8DCC0',color:'#1E5C40',padding:'10px 14px',borderRadius:8,fontSize:13,marginBottom:16 }}>✓ Reminder emails sent successfully!</div>}

      {faculty.length>0&&(
        <div style={{ background:'#fff',borderRadius:12,padding: isMobile?14:'20px 24px',marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={s.cardTitle}>Faculty Members</h3>
          <div style={{ display:'flex',gap:10,flexWrap:'wrap',marginTop:12 }}>
            {faculty.map(f=>(
              <div key={f.id} style={{ background:'#EEF5FB',border:'1px solid #A8C8DC',borderRadius:8,padding:'8px 14px' }}>
                <div style={{ fontSize:13,fontWeight:600,color:'#2D6A8A' }}>{f.full_name}</div>
                <div style={{ fontSize:11,color:'#888',marginTop:2 }}>{allEntries.filter(e=>(e.assigned_faculty_names||[]).includes(f.full_name)).length} cases assigned</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
        {fellows.map(fellow=>{
          const fe=fellowEntries(fellow.id);
          const approved=fe.filter(e=>['approved','approved_with_comments'].includes(e.approval_status)).length;
          const pending=fe.filter(e=>e.approval_status==='pending').length;
          const rejected=fe.filter(e=>e.approval_status==='rejected').length;
          const fellowActs=allActivities.filter(a=>a.user_id===fellow.id).length;
          return(
            <div key={fellow.id} style={{ background:'#fff',borderRadius:12,padding: isMobile?'14px':'16px 20px',boxShadow:'0 2px 8px rgba(0,0,0,0.05)',display:'flex',alignItems:'center',gap:14 }}>
              <div onClick={()=>setSelectedFellow(selectedFellow?.id===fellow.id?null:fellow)} style={{ width:40,height:40,borderRadius:'50%',background:'#2D6A8A',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Serif Display'",fontSize:18,color:'#fff',flexShrink:0,cursor:'pointer' }}>{fellow.full_name?.[0]||'?'}</div>
              <div style={{ flex:1,cursor:'pointer' }} onClick={()=>setSelectedFellow(selectedFellow?.id===fellow.id?null:fellow)}>
                <div style={{ fontSize:15,fontWeight:600,color:'#1A1A1F' }}>{fellow.full_name}</div>
                <div style={{ display:'flex',gap:10,marginTop:4,flexWrap:'wrap' }}>
                  <span style={{ fontSize:11,color:'#1E5C40',fontWeight:600 }}>✓ {approved} approved</span>
                  {pending>0&&<span style={{ fontSize:11,color:'#C4622A',fontWeight:600 }}>⏳ {pending} pending</span>}
                  {rejected>0&&<span style={{ fontSize:11,color:'#8A2D2D',fontWeight:600 }}>✗ {rejected} rejected</span>}
                  <span style={{ fontSize:11,color:'#6B4D8A',fontWeight:600 }}>📋 {fellowActs} activities</span>
                </div>
              </div>
              <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8 }}>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontFamily:"'DM Serif Display'",fontSize:22,color:'#2D6A8A' }}>{fe.length}</div>
                  <div style={{ fontSize:11,color:'#999' }}>cases · Avg {avgLevel(fellow.id)}</div>
                </div>
                {!isChairman&&(
                  <button onClick={e=>{e.stopPropagation();sendReminder(fellow);}} disabled={sendingReminder===fellow.id}
                    style={{ padding:'6px 12px',background:'#EEF5FB',color:'#2D6A8A',border:'1px solid #A8C8DC',borderRadius:8,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans'",whiteSpace:'nowrap' }}>
                    {sendingReminder===fellow.id?'⏳ Sending…':'📧 Remind'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {fellows.length===0&&<div style={s.empty}>No fellows have signed up yet.</div>}
      </div>

      {selectedFellow&&(
        <div style={{ marginTop:24 }}>
          <h3 style={{ fontFamily:"'DM Serif Display'",fontSize:18,color:'#1A1A1F',marginBottom:14 }}>{selectedFellow.full_name} — Competency Overview</h3>
          <div style={{ display:'grid',gridTemplateColumns: isMobile?'1fr 1fr':'repeat(3,1fr)',gap:10 }}>
            {COMPETENCIES.map(c=>{const count=compCount(selectedFellow.id,c.id);const pct=Math.min(100,Math.round((count/c.min)*100));return(
              <div key={c.id} style={{ background:'#fff',borderRadius:10,padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',borderTop:`3px solid ${c.color}` }}>
                <div style={{ fontSize:12,fontWeight:600,color:'#1a1a1a',marginBottom:8 }}>{c.label}</div>
                <div style={{ display:'flex',alignItems:'baseline',gap:4,marginBottom:6 }}><span style={{ fontFamily:"'DM Serif Display'",fontSize:22,color:c.color }}>{count}</span><span style={{ fontSize:12,color:'#888' }}>/ {c.min}</span></div>
                <div style={{ height:4,background:'#EEE',borderRadius:10,overflow:'hidden' }}><div style={{ height:'100%',borderRadius:10,width:`${pct}%`,background:pct>=100?'#1E5C40':c.color }}/></div>
              </div>
            );})}
          </div>
        </div>
      )}
    </div>
  );
}

function DecisionPanel({decision,setDecision,comment,setComment,saving,onSubmit,includeApprovedWithComments}) {
  const options=[
    {val:'approved',label:'✓ Approved',desc:'Meets requirements',border:'#A8DCC0',bg:'#F0FFF6',active:'#1E5C40'},
    ...(includeApprovedWithComments?[{val:'approved_with_comments',label:'✓ Approved with Comments',desc:'Passed but feedback given',border:'#A8C8DC',bg:'#EEF5FB',active:'#2D6A8A'}]:[]),
    {val:'rejected',label:'✗ Rejected',desc:'Does not meet requirements',border:'#F4A8A8',bg:'#FFF0F0',active:'#8A2D2D'},
  ];
  return(
    <div style={{ background:'#fff',borderRadius:12,padding:'20px 24px',boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontFamily:"'DM Serif Display'",fontSize:17,color:'#1A1A1F',marginBottom:14 }}>Your Decision</div>
      <div style={{ display:'flex',flexDirection:'column',gap:10,marginBottom:16 }}>
        {options.map(opt=>(
          <button key={opt.val} onClick={()=>setDecision(opt.val)} style={{ padding:'12px 16px',borderRadius:10,border:`1.5px solid ${decision===opt.val?opt.active:opt.border}`,background:decision===opt.val?opt.active:opt.bg,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans'" }}>
            <div style={{ fontWeight:600,fontSize:13,color:decision===opt.val?'#fff':opt.active }}>{opt.label}</div>
            <div style={{ fontSize:11,marginTop:3,color:decision===opt.val?'rgba(255,255,255,0.75)':'#888' }}>{opt.desc}</div>
          </button>
        ))}
      </div>
      <textarea value={comment} rows={3} placeholder="Comment…" onChange={e=>setComment(e.target.value)} style={{ width:'100%',padding:'10px 13px',border:'1.5px solid #E8E4E0',borderRadius:8,fontSize:13,fontFamily:"'DM Sans'",resize:'vertical',outline:'none',boxSizing:'border-box',marginBottom:14 }}/>
      <button onClick={onSubmit} disabled={!decision||saving} style={{ width:'100%',padding:'13px',background:decision?'#1A3A5C':'#CCC',color:'#F5F0E8',border:'none',borderRadius:8,fontSize:14,fontWeight:600,cursor:decision?'pointer':'default',fontFamily:"'DM Sans'" }}>
        {saving?'Submitting…':'Submit Decision'}
      </button>
    </div>
  );
}

const s = {
  loading:{padding:60,textAlign:'center',color:'#888',fontFamily:"'DM Sans'"},
  title:{fontFamily:"'DM Serif Display'",fontSize:28,color:'#1A1A1F',marginBottom:4},
  sub:{fontSize:13,color:'#888',marginBottom:20},
  metricCard:{background:'#fff',borderRadius:12,padding:'18px 20px',boxShadow:'0 2px 8px rgba(0,0,0,0.05)'},
  metricVal:{fontFamily:"'DM Serif Display'",fontSize:32,color:'#1A1A1F',marginBottom:4},
  metricLabel:{fontSize:13,fontWeight:600,color:'#555'},
  cardTitle:{fontFamily:"'DM Serif Display'",fontSize:17,color:'#1A1A1F'},
  difficultBadge:{fontSize:10,background:'#FFF0E8',color:'#C4622A',padding:'2px 8px',borderRadius:20,fontWeight:600,border:'1px solid #F4C8A8'},
  backBtn:{background:'#EEE',color:'#444',border:'none',borderRadius:8,padding:'8px 14px',fontSize:13,cursor:'pointer',fontFamily:"'DM Sans'"},
  empty:{textAlign:'center',padding:'40px 0',color:'#aaa',fontSize:14},
};
